﻿import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { CatalogTypes } from "@/components/Maintenance/MaintenanceTypes";
import dayjs from "dayjs";
import axios from "@/features/axiosHelper";

const maintenanceSlice = createSlice({
  name: "maintenance",
  initialState: {
    dateFilter: {
      startDate: dayjs().subtract(7, "day").startOf("day"),
      endDate: dayjs().add(1, "day").startOf("day").subtract(1, "minute"),
    },
    catalogSummary: {
      records: [],
      recordCount: 0,
      recordsLoaded: false,
      loadingError: null,
      filters: {
        type: [],
      },
      selectedTab: CatalogTypes.Customer,
      searchText: "",
      customFields: [],
      customFieldsLoaded: false,
    },
    crossReferenceSummary: {
      records: [],
      recordCache: {},
      recordsLoaded: false,
      loadingError: null,
      filters: {
        type: [],
      },
      selectedTab: 0,
      selectedConnectionPair: {},
    },
    connectionQualifiers: {
      qualifiers: [],
      qualifiersLoaded: true,
      loadingError: null,
    },
    connectionPairDetails: [],
    connectionPairStatus: "idle",

    // custom fields
    customFields: {
      selectedConnectionCustomField: [],
      selectedConnectionId: null,
      allCustomFields: [],
      allConnections: [],
      allCustomFieldsAndValues: [],
    },
  },

  reducers: {
    setDateFilter: (state, action) => {
      const { dateFilter } = action.payload;
      state.dateFilter = dateFilter;
    },
    setCatalogSummaryFilterTypes: (state, action) => {
      const { value } = action.payload;
      state.catalogSummary.filters.type = value;
    },
    setCrossReferenceSummaryFilterTypes: (state, action) => {
      const { value } = action.payload;
      state.crossReferenceSummary.filters.type = value;
    },
    setSelectedConnectionPair: (state, action) => {
      const { selectedConnectionPair } = action.payload;
      state.crossReferenceSummary.selectedConnectionPair =
        selectedConnectionPair;
    },
    setCatalogSummarySelectedTab: (state, action) => {
      const { selectedTab } = action.payload;
      state.catalogSummary.selectedTab = selectedTab;
    },
    setCatalogSummarySearchText: (state, action) => {
      state.catalogSummary.searchText = action.payload;
    },
    setCrossReferenceSummarySelectedTab: (state, action) => {
      const { selectedTab } = action.payload;
      state.crossReferenceSummary.selectedTab = selectedTab;
    },
    setCatalogCustomFields: (state, action) => {
      const { customFields } = action.payload;
      state.catalogSummary.customFields = customFields;
    },
    deleteCrossReferenceSummaryRecords: (state, action) => {
      const idsToDelete = action.payload;
      state.crossReferenceSummary.records =
        state.crossReferenceSummary.records.filter(
          (record) => !idsToDelete.includes(record.crossReferenceMappingId),
        );
    },
    clearAllRecordCaches: (state) => {
      state.crossReferenceSummary.recordCache = {};
      state.crossReferenceSummary.records = [];
    },
    clearTabCache: (state, action) => {
      const selectedTabIdToClear = action.payload;
      state.crossReferenceSummary.recordCache[selectedTabIdToClear] = null;
    },

    // custom fields
    setSelectedCustomField: (state, action) => {
      const { selectedCustomField } = action.payload;
      state.customFields.selectedConnectionCustomField = selectedCustomField;
    },

    setSelectedConnectionId: (state, action) => {
      const { selectedConnectionId } = action.payload;
      state.customFields.selectedConnectionId = selectedConnectionId;
    },

    setAllCustomFields: (state, action) => {
      const { allCustomFields } = action.payload;
      state.customFields.allCustomFields = allCustomFields;
    },

    setAllConnections: (state, action) => {
      const { allConnections } = action.payload;
      state.customFields.allConnections = allConnections;
    },

    setAllCustomFieldsAndValues: (state, action) => {
      const { allCustomFieldsAndValues } = action.payload;
      state.customFields.allCustomFieldsAndValues = allCustomFieldsAndValues;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchItemQualifiers.pending, (state, action) => {
        state.connectionQualifiers.qualifiersLoaded = false;
      })
      .addCase(fetchItemQualifiers.rejected, (state, action) => {
        state.connectionQualifiers.qualifiersLoaded = true;
        state.connectionQualifiers.loadingError = action.error.message;
      })
      .addCase(fetchItemQualifiers.fulfilled, (state, action) => {
        state.connectionQualifiers.qualifiersLoaded = true;
        state.connectionQualifiers.qualifiers = action.payload;
      })
      .addCase(fetchCatalogSummaries.pending, (state, action) => {
        if (action.meta.arg.unloadRecords) {
          state.catalogSummary.recordsLoaded = false;
          state.catalogSummary.records = [];
          return;
        }

        // Only show loading screen if there are missing catalog items
        const pagination = action.meta.arg;
        const page = pagination.page ?? 0;
        for (
          let i = page * pagination.pageSize;
          i < (page + 1) * pagination.pageSize - 1;
          i++
        ) {
          if (state.catalogSummary.records[i] == null) {
            state.catalogSummary.recordsLoaded = false;
          }
        }
      })
      .addCase(fetchCatalogSummaries.rejected, (state, action) => {
        state.catalogSummary.recordsLoaded = true;
        state.catalogSummary.loadingError = action.error.message;
      })
      .addCase(fetchCatalogSummaries.fulfilled, (state, action) => {
        const newCatalog = action.payload.unloadRecords
          ? []
          : state.catalogSummary.records;

        // Padding of not yet loaded records
        while (action.payload.startIndex > newCatalog.length) {
          newCatalog.push(undefined);
        }

        // Patch records from api request
        state.catalogSummary.records = newCatalog.toSpliced(
          action.payload.startIndex,
          action.payload.records.length,
          ...action.payload.records,
        );

        state.catalogSummary.recordCount = action.payload.totalCount;
        state.catalogSummary.recordsLoaded = true;
      })
      .addCase(fetchCrossReferenceSummaries.pending, (state, action) => {
        const tabId = action.meta.arg.crossReferenceType ?? -1;
        const isSearching = action.meta.arg.searchText;
        if (state.crossReferenceSummary.recordCache[tabId] && !isSearching) {
          state.crossReferenceSummary.records =
            state.crossReferenceSummary.recordCache[tabId];
          state.crossReferenceSummary.recordsLoaded = true;
        } else {
          state.crossReferenceSummary.recordsLoaded = false;
          state.crossReferenceSummary.records = [];
        }
      })
      .addCase(fetchCrossReferenceSummaries.rejected, (state, action) => {
        state.crossReferenceSummary.recordsLoaded = true;
        state.crossReferenceSummary.loadingError = action.error.message;
      })
      .addCase(fetchCrossReferenceSummaries.fulfilled, (state, action) => {
        const tabId = action.meta.arg.crossReferenceType ?? -1;
        const dataToSave = action.payload.map((record) => ({
          ...record,
          id: record.CrossReferenceMappingId,
        }));
        const isSearching = action.meta.arg.searchText;
        state.crossReferenceSummary.records = dataToSave;
        if (!isSearching) {
          state.crossReferenceSummary.recordCache[tabId] = dataToSave;
        }
        state.crossReferenceSummary.recordsLoaded = true;
      })
      .addCase(fetchConnectionPairs.pending, (state, action) => {
        state.connectionPairStatus = "loading";
      })
      .addCase(fetchConnectionPairs.rejected, (state, action) => {
        state.connectionPairStatus = "idle - failed";
        state.error = action.error.message;
      })
      .addCase(fetchConnectionPairs.fulfilled, (state, action) => {
        state.connectionPairStatus = "idle - loaded";
        state.connectionPairDetails = action.payload;
      })
      .addCase(fetchCatalogCustomFields.pending, (state, action) => {
        state.catalogSummary.customFieldsLoaded = false;
      })
      .addCase(fetchCatalogCustomFields.rejected, (state, action) => {
        state.catalogSummary.customFieldsLoaded = true;
      })
      .addCase(fetchCatalogCustomFields.fulfilled, (state, action) => {
        state.catalogSummary.customFieldsLoaded = true;
        state.catalogSummary.customFields = action.payload;
      });
  },
});

export const fetchItemQualifiers = createAsyncThunk(
  "maintenance/catalog-qualifiers",
  async ({ connectionId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        "/connectioncatalogartifacts/qualifiers",
        {
          params: { connectionId },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching catalogue identifiers:", error);
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchCatalogSummaries = createAsyncThunk(
  "maintenance/catalog-summary",
  async (
    { page = 0, pageSize, search, unloadRecords = false },
    { getState },
  ) => {
    const connectionId =
      getState().tempInfo.integration.selectedConnectionInfo[0];
    const catalogArtifactType =
      getState().tempInfo.maintenance?.catalogSummary?.selectedTab;

    if (!connectionId) {
      return {
        records: [],
        totalCount: 0,
      };
    }

    const response = await axios.get("/connectioncatalogartifacts/summary", {
      params: {
        connectionId,
        artifactType: catalogArtifactType,
        offset: page * pageSize,
        count: pageSize,
        search: search,
      },
    });

    // Include start index & unload flag
    response.data.startIndex = page * pageSize;
    response.data.unloadRecords = unloadRecords;
    return response.data;
  },
);

export const fetchCrossReferenceSummaries = createAsyncThunk(
  "maintenance/cross-reference-summary",
  async (
    { connectionPairId, searchText, crossReferenceType = -1 },
    { rejectWithValue, getState },
  ) => {
    try {
      const tabId = crossReferenceType ?? -1;
      const existingCache =
        getState().tempInfo.maintenance.crossReferenceSummary.recordCache[
          tabId
        ];
      //if data exists in cache and user isn't searching, skip backend call
      if (existingCache && !searchText) {
        return existingCache;
      }

      const response = await axios.get("/crossreference/summary", {
        params: {
          connectionPair: connectionPairId,
          searchText: searchText,
          crossReferenceType: crossReferenceType,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching cross-reference summaries:", error);
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchConnectionPairs = createAsyncThunk(
  "maintenance/fetchConnectionPairs",
  async (params) => {
    const response = await axios.get("/connectionpair", {
      params: {
        clearCache: false,
      },
    });
    return response.data;
  },
);

export const fetchCatalogCustomFields = createAsyncThunk(
  "maintenance/fetchCatalogCustomFields",
  async (params) => {
    const response = await axios.get(
      "/connectioncatalogartifacts/custom-fields",
    );
    return response.data;
  },
);

export const selectDateFilter = (state) => state.maintenance?.dateFilter || {};
export const selectCatalogSummaryFilterTypes = (state) =>
  state.tempInfo.maintenance?.catalogSummary?.filters?.type || [];
export const selectConnectionQualifiers = (state) =>
  state.tempInfo.maintenance?.connectionQualifiers.qualifiers || [];
export const selectConnectionQualifiersLoaded = (state) =>
  state.tempInfo.maintenance?.connectionQualifiers.qualifiersLoaded || false;
export const selectCatalogSummaryRecordsLoaded = (state) =>
  state.tempInfo.maintenance?.catalogSummary?.recordsLoaded || false;
export const selectCatalogSummaryRecords = (state) =>
  state.tempInfo.maintenance?.catalogSummary?.records || [];
export const selectCatalogSummaryRecordCount = (state) =>
  state.tempInfo.maintenance?.catalogSummary?.recordCount || 0;
export const selectCatalogSummarySelectedTab = (state) =>
  state.tempInfo.maintenance?.catalogSummary?.selectedTab;
export const selectCatalogSummarySearchText = (state) =>
  state.tempInfo.maintenance?.catalogSummary?.searchText || "";
export const selectCrossReferenceSummaryFilterTypes = (state) =>
  state.tempInfo.maintenance?.crossReferenceSummary?.filters?.type || [];
export const selectCrossReferenceSummaryRecordsLoaded = (state) =>
  state.tempInfo.maintenance?.crossReferenceSummary?.recordsLoaded || false;
export const selectCrossReferenceSummaryRecords = (state) =>
  state.tempInfo.maintenance?.crossReferenceSummary?.records || [];
export const selectCrossReferenceSummarySelectedTab = (state) =>
  state.tempInfo.maintenance?.crossReferenceSummary?.selectedTab || 0;
export const selectSelectedConnectionPair = (state) =>
  state.tempInfo.maintenance?.crossReferenceSummary?.selectedConnectionPair ||
  {};
export const selectConnectionPairDetails = (state) =>
  state.tempInfo.maintenance.connectionPairDetails;
export const selectAllCatalogCustomFields = (state) =>
  state.tempInfo.maintenance?.catalogSummary?.customFields ?? [];
export const selectCatalogCustomFieldsLoaded = (state) =>
  state.tempInfo.maintenance.catalogSummary.customFieldsLoaded;

// custom fields
export const getSelectedCustomField = (state) =>
  state.tempInfo.maintenance?.customFields?.selectedConnectionCustomField;
export const getSelectedConnectionId = (state) =>
  state.tempInfo.maintenance?.customFields?.selectedConnectionId;
export const getAllCustomFields = (state) =>
  state.tempInfo.maintenance?.customFields?.allCustomFields;
export const getAllConnections = (state) =>
  state.tempInfo.maintenance?.customFields?.allConnections;
export const getAllCustomFieldsAndValues = (state) =>
  state.tempInfo.maintenance?.customFields?.allCustomFieldsAndValues;

export const {
  setDateFilter,
  setCatalogSummaryFilterTypes,
  setCrossReferenceSummaryFilterTypes,
  setSelectedConnectionPair,
  setCatalogSummarySelectedTab,
  setCatalogSummarySearchText,
  setCrossReferenceSummarySelectedTab,
  deleteCrossReferenceSummaryRecords,
  clearAllRecordCaches,
  clearTabCache,
  setSelectedCustomField,
  setSelectedConnectionId,
  setAllCustomFields,
  setAllConnections,
  setAllCustomFieldsAndValues,
  setCatalogCustomFields,
} = maintenanceSlice.actions;

export default maintenanceSlice.reducer;
